

import type { TrackingEvent, TrackResponse } from '../types';
import type { ResolvedConfig } from './config';
import { buildApiUrl } from './config';

export interface QueueOptions {
  config: ResolvedConfig;
  onFlush?: (events: TrackingEvent[], response: TrackResponse) => void;
  onError?: (error: Error, events: TrackingEvent[]) => void;
}

export class EventQueue {
  private queue: TrackingEvent[] = [];
  private config: ResolvedConfig;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private isFlushing = false;
  private onFlush?: (events: TrackingEvent[], response: TrackResponse) => void;
  private onError?: (error: Error, events: TrackingEvent[]) => void;

  constructor(options: QueueOptions) {
    this.config = options.config;
    this.onFlush = options.onFlush;
    this.onError = options.onError;

    
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flushSync());
      window.addEventListener('pagehide', () => this.flushSync());
    }
  }

  
  push(event: TrackingEvent): void {
    this.queue.push(event);

    this.log(`Event queued: ${event.type}`, event);

    
    if (this.queue.length >= this.config.batchSize) {
      this.flush();
    } else {
      
      this.scheduleFlush();
    }
  }

  
  private scheduleFlush(): void {
    if (this.flushTimer) return;

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, this.config.flushInterval);
  }

  
  async flush(): Promise<void> {
    if (this.isFlushing || this.queue.length === 0) return;

    this.isFlushing = true;

    
    const events = [...this.queue];
    this.queue = [];

    
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    try {
      const response = await this.sendEvents(events);
      this.log(`Flushed ${events.length} events`, response);
      this.onFlush?.(events, response);
    } catch (error) {
      this.log('Flush failed', error);
      this.onError?.(error as Error, events);

      
      if (this.queue.length < this.config.batchSize * 5) {
        this.queue = [...events, ...this.queue];
      }
    } finally {
      this.isFlushing = false;
    }
  }

  
  flushSync(): void {
    if (this.queue.length === 0) return;

    const events = [...this.queue];
    this.queue = [];

    
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const url = buildApiUrl(this.config, '/track');
      const data = JSON.stringify({ events });
      
      const blob = new Blob([data], { type: 'text/plain' });

      
      
      const beaconUrl = `${url}?apiKey=${encodeURIComponent(this.config.apiKey)}`;

      const sent = navigator.sendBeacon(beaconUrl, blob);
      this.log(`sendBeacon ${sent ? 'succeeded' : 'failed'}: ${events.length} events`);
    } else {
      
      this.sendEventsSync(events);
    }
  }

  
  private async sendEvents(events: TrackingEvent[]): Promise<TrackResponse> {
    const url = buildApiUrl(this.config, '/track');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.config.apiKey
      },
      body: JSON.stringify({ events })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  
  private sendEventsSync(events: TrackingEvent[]): void {
    try {
      const url = buildApiUrl(this.config, '/track');
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, false); 
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('X-API-Key', this.config.apiKey);
      xhr.send(JSON.stringify({ events }));
    } catch (error) {
      this.log('Sync XHR failed', error);
    }
  }

  
  get length(): number {
    return this.queue.length;
  }

  
  clear(): void {
    this.queue = [];
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  
  private log(message: string, data?: unknown): void {
    if (this.config.debug) {
      console.log(`[Twiggy] ${message}`, data || '');
    }
  }
}
